import {CommonModule, TitleCasePipe} from '@angular/common';
import {AfterViewInit, Component, OnInit, computed, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatButtonModule} from '@angular/material/button';
import {MatDividerModule} from '@angular/material/divider';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ActivatedRoute, Router, RouterOutlet} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {
  BillingDaoService,
  ConfigService,
  IntercomService,
  PermissionsService,
  REPORT_MODAL_TYPE,
  SessionService,
  User
} from '@q9elements/ui-kit/common';
import {
  IconButtonComponent,
  LoadingPlaceholderModule,
  NotificationsModule,
  PrimaryButtonComponent,
  ReportsModule,
  SidenavModule,
  ThemeSwitcherComponent,
  ToolbarModule,
  TrainingsModule
} from '@q9elements/ui-kit/components';
import {get} from 'lodash';
import {map} from 'rxjs';

import {LicenseType, MODULES} from '../../models/navigation';
import {NavigationService} from '../../services/navigation.service';
import {SpaceInviteService} from '../../services/space-invite.service';
import {SpaceNavigationComponent} from '../space-navigation/space-navigation.component';
import {CommonWrapperDialogService} from './services/common-wrapper-dialog.service';
import {ROUTES_PATH} from '../../models/routes-path.enum';

@Component({
  selector: 'q9ma-common-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    LoadingPlaceholderModule,
    ToolbarModule,
    MatMenuModule,
    SidenavModule,
    TranslateModule,
    NotificationsModule,
    TrainingsModule,
    ReportsModule,
    SpaceNavigationComponent,
    IconButtonComponent,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    ThemeSwitcherComponent,
    MatTooltipModule,
    PrimaryButtonComponent,
    TitleCasePipe
  ],
  providers: [CommonWrapperDialogService],
  templateUrl: './common-wrapper.component.html',
  styleUrls: ['./common-wrapper.component.scss']
})
export class CommonWrapperComponent implements AfterViewInit, OnInit {
  private readonly bookACallUrl = 'https://elements.cloud/book-a-call/?step=1';
  readonly licenseFlags: Record<LicenseType, Signal<boolean>> = {
    urlLibraryLicense: this.permissionsService.hasUrlLibraryLicense
  };
  // readonly decisionEngineLicense = computed(() =>
  //   get(this.permissionsService.team(), 'licenseFlags.decisionEngine', false)
  // );
  readonly availableModules = computed(() =>
    MODULES.filter(module => {
      if (!module.requires?.length) {
        return true;
      }

      return module.requires.every(req => {
        const flag = this.licenseFlags[req];

        return flag ? flag() : false;
      });
    })
  );
  readonly isFree = this.permissionsService.isFree;
  readonly isMsSpace = this.permissionsService.isMsSpace;
  readonly isSysAdmin = this.permissionsService.isSysAdmin;
  readonly isProActive = this.permissionsService.isProActive;
  readonly isEditor = this.permissionsService.isEditor;

  readonly hasDecisionEngineLicense = this.permissionsService.hasDecisionEngineLicense;
  readonly elementsGPTEnabledInSpace = this.permissionsService.elementsGPTEnabledInSpace;

  readonly hasAccessToEnterprise = this.checkAccessToEnterprise();
  readonly user = this.sessionService.user as Signal<User>;
  readonly isSpaceAdmin = this.permissionsService.isTeamAdmin;
  readonly hasSubscription = computed(() => {
    const user = this.user();

    return !!get(user, 'userInTeam.team.subscription.subscriptionId');
  });
  readonly canInviteUsers = computed(() => {
    const currentSpace = this.permissionsService.team;
    const closedSpaceForNonAdmin =
      !this.isSpaceAdmin() &&
      this.permissionsService.isProSpace() &&
      currentSpace()?.adminControlSpaceInvites;

    return !this.permissionsService.isPlayground() && !closedSpaceForNonAdmin;
  });

  constructor(
    private route: ActivatedRoute,
    private sessionService: SessionService,
    private intercomService: IntercomService,
    private navigationService: NavigationService,
    private permissionsService: PermissionsService,
    private commonWrapperDialogService: CommonWrapperDialogService,
    private router: Router,
    private snackBar: MatSnackBar,
    private billing: BillingDaoService,
    private spaceInviteService: SpaceInviteService
  ) {}

  ngOnInit() {
    this.handleCheckoutQueryParams();
    this.handleBillingAction();
  }

  ngAfterViewInit() {
    const user = <User>this.sessionService.user();

    this.intercomService.bootIntercom(user);
  }

  private handleCheckoutQueryParams() {
    const checkoutResult = this.route.snapshot.queryParams['checkout'];

    if (!checkoutResult) {
      return;
    }

    if (checkoutResult === 'success') {
      this.snackBar.open(`Your subscription plan has been successfully updated!`, undefined, {
        duration: 5000
      });
    } else if (checkoutResult === 'cancel') {
      this.snackBar.open(
        `Checkout was cancelled. Your subscription remains unchanged.`,
        undefined,
        {duration: 4000}
      );
    }

    this.clearCheckoutQueryParams();
  }

  private clearCheckoutQueryParams() {
    const queryParams = {...this.route.snapshot.queryParams};

    delete queryParams['checkout'];

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    });
  }

  private handleBillingAction() {
    const params = this.route.snapshot.queryParams;
    const billingAction = params['billingAction'];
    const user = this.sessionService.user();

    if (!billingAction || !user) {
      return;
    }

    const hasSubscription = get(user, 'userInTeam.team.subscription.planType', 'free') !== 'free';
    const isDelinquent = get(user, 'userInTeam.team.subscription.delinquent', false);

    this.clearBillingActionQueryParam();

    if (billingAction === 'billing') {
      if (isDelinquent || hasSubscription) {
        this.openBillingPortal();
      } else {
        this.openPricingCalculator();
      }
    }
  }

  private clearBillingActionQueryParam() {
    const queryParams = {...this.route.snapshot.queryParams};

    delete queryParams['billingAction'];

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    });
  }

  private checkAccessToEnterprise() {
    return toSignal(
      this.route.data.pipe(
        map(({userEnterprisePermissions}) => {
          const hasAccess = get(userEnterprisePermissions, 'hasAccess', false);
          const enterpriseExists = get(userEnterprisePermissions, 'enterpriseExists', false);

          return enterpriseExists && hasAccess;
        })
      )
    );
  }

  navigateTo(path?: string) {
    return this.navigationService.navigateToModule(path);
  }

  navigateToDecisionEngine() {
    const urlTree = this.router.createUrlTree([
      't',
      this.sessionService.teamIndex(),
      ROUTES_PATH.DECISION_ENGINE
    ]);

    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  openSpaceManagementApp() {
    const url = [ConfigService.environment.TEAM_APP, 't', this.sessionService.teamIndex()].join(
      '/'
    );

    window.open(url, '_blank');
  }

  openSysAdminApp() {
    const url = `${ConfigService.environment.SYS_ADMIN_APP}/spaces`;

    window.open(url, '_blank');
  }

  openCorporateManagementApp() {
    window.open(ConfigService.environment.ENTERPRISE_URL, '_blank');
  }

  openUsageSettings() {
    this.navigateTo(ROUTES_PATH.USAGE);
  }

  public onChange(modalType: REPORT_MODAL_TYPE) {
    this.commonWrapperDialogService.openReportDialog(modalType);
  }

  openPricingCalculator() {
    if (this.isMsSpace()) {
      this.commonWrapperDialogService.openPricingCalculator();

      return;
    }

    window.open(this.bookACallUrl, '_blank');
  }

  openInviteUser() {
    this.spaceInviteService.openInviteForCurrentSpaceDialog();
  }

  openBillingPortal() {
    const {origin, pathname} = window.location;
    const returnUrl = `${origin}${pathname}`;

    this.billing.createPortalSession({returnUrl}).subscribe({
      next: response => {
        window.location.href = response.url;
      },
      error: err => {
        console.error('Failed to create portal session:', err);
        this.snackBar.open($localize`Failed to open billing portal. Please try again.`, undefined, {
          duration: 4000
        });
      }
    });
  }
}
